import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Orientation } from "@westpierstudio/luma/layout";
import { Tween } from "@westpierstudio/luma/createjs";
import { AssetSpec, SpriteAssetSpec, TextAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { Signal } from "type-signals";
import { Text } from "pixi.js";
import { Bounds, implementBoundedContainer } from "utils/BoundedContainer";

export interface FreeSpinCounterSetting {
    totalCountupTime: number
}

class FreeSpinCounterSignals {
    totalCounterIncrement = new Signal();
    totalCountUpComplete = new Signal();
}

interface FreeSpinCounterSpec extends AssetSpec {
    freeSpinCounterLandscape: AssetSpec,
    freeSpinCounterPortrait: AssetSpec,
    freeSpinCounterHeaderBounds: Bounds
    panel: SpriteAssetSpec,
    freeSpinCounterHeader: TextAssetSpec
    freeSpinCounterOf: TextAssetSpec,
    freeSpinCounterOfExpandWidth: number,
    freeSpinCounterCurrent: TextAssetSpec,
    freeSpinCounterTotal: TextAssetSpec,
    threeDigitScale: number
}

export class FreeSpinCounter extends View {
    private spec: FreeSpinCounterSpec;
    private currentValue!: Text;
    private totalValue!: Text;
    signals = new FreeSpinCounterSignals();

    constructor(services: GameServices) {
        super(services);

        const { factory, resources } = this.services;

        const spec = this.spec = resources.getSpec(`freespincounter`) as FreeSpinCounterSpec;

        factory.createSprite(spec.panel, this);
        const header = factory.createText(resources.getMessage(`freeSpinCounterHeader`), spec.freeSpinCounterHeader, this);
        implementBoundedContainer(header, spec.freeSpinCounterHeaderBounds);
        const ofText = factory.createText(resources.getMessage(`freeSpinCounterOf`), this.spec.freeSpinCounterOf, this);

        this.currentValue = factory.createText(``, spec.freeSpinCounterCurrent, this);
        this.totalValue = factory.createText(``, spec.freeSpinCounterTotal, this);

        const offset = ofText.width - spec.freeSpinCounterOfExpandWidth;
        if (offset > 0) {
            this.currentValue.x -= offset / 2;
            this.totalValue.x += offset / 2;
        }

        this.visible = false;
    }

    setFreeSpins(remaining: number, total: number): void {

        const { resources } = this.services;

        this.currentValue.text = resources.getMessage(`freeSpinCounterCurrent`, { current: (total - remaining).toString() });
        this.totalValue.text = resources.getMessage(`freeSpinCounterTotal`, { total: total.toString() });

        this.scaleToFitText(this.currentValue);
        this.scaleToFitText(this.totalValue);
    }

    countUpToTotalFreeSpins(remaining: number, total: number): void {

        const { resources } = this.services;

        const totalCountupTime = (resources.getSetting(`freeSpinCounter`) as FreeSpinCounterSetting).totalCountupTime;
        this.currentValue.text = resources.getMessage(`freeSpinCounterCurrent`, { current: (total - remaining).toString() });
        this.scaleToFitText(this.currentValue);

        const currentFreeSpinTotal = this.totalValue.text === `` ? 0 : parseInt(this.totalValue.text);
        const countUpPerDigitDelay = totalCountupTime / (total - currentFreeSpinTotal);
        const tween = Tween.get(this);

        for (let i = currentFreeSpinTotal; i <= total; i++) {

            tween.call(() => {
                this.totalValue.text = this.services.resources.getMessage(`freeSpinCounterTotal`, { total: i.toString() });
                this.scaleToFitText(this.totalValue);
                this.signals.totalCounterIncrement.dispatch();
            });

            if (i === total) {
                tween.call(() => this.signals.totalCountUpComplete.dispatch());
            } else {
                tween.wait(countUpPerDigitDelay);
            }
        }
    }

    skip(total: number): void {

        Tween.removeTweens(this);

        this.totalValue.text = this.services.resources.getMessage(`freeSpinCounterTotal`, { total: total.toString() });
        this.scaleToFitText(this.totalValue);
        this.signals.totalCountUpComplete.dispatch();
    }

    resize(orientation: Orientation): void {

        const { factory } = this.services;

        if (orientation === `landscape`) {
            factory.applySpec(this, this.spec.freeSpinCounterLandscape);
        } else {
            factory.applySpec(this, this.spec.freeSpinCounterPortrait);
        }
    }

    override reset(): void {

        Tween.removeTweens(this);

        this.currentValue.text = ``;
        this.totalValue.text = ``;

        this.visible = false;
    }

    private scaleToFitText(text: Text): void {

        if (text.text.length === 3) {
            text.scale.set(this.spec.threeDigitScale, this.spec.threeDigitScale);
        } else {
            text.scale.set(1, 1);
        }
    }
}